import { useCallback, useEffect, useRef } from "react";

export const PTZ_ZOOM_WHEEL_END_MS = 400;

function wheelDelta(e: WheelEvent): number {
  const dy = e.deltaY;
  const dx = e.deltaX;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy < 0 ? 1 : dy > 0 ? -1 : 0;
  }
  return dx < 0 ? 1 : dx > 0 ? -1 : 0;
}

export type UsePtzWheelZoomOptions = {
  operable: boolean;
  min: number;
  max: number;
  /** Current zoom from server / PTZ state (slider must not move until API responds). */
  getZoom: () => number;
  commitZoom: (zoom: number) => void;
};

/**
 * Mouse-wheel PTZ zoom: no slider movement while scrolling.
 * After scroll stops (~400ms), one API call; UI updates when the response updates getZoom().
 */
export function usePtzWheelZoom({
  operable,
  min,
  max,
  getZoom,
  commitZoom,
}: UsePtzWheelZoomOptions) {
  const pendingTargetRef = useRef<number | null>(null);
  const wheelEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(max, Math.max(min, Math.round(value))),
    [min, max],
  );

  const onWheelNative = useCallback(
    (e: WheelEvent) => {
      if (!operable) return;
      e.preventDefault();
      e.stopPropagation();

      const delta = wheelDelta(e);
      if (delta === 0) return;

      const base =
        pendingTargetRef.current !== null
          ? pendingTargetRef.current
          : clamp(getZoom());
      const next = clamp(base + delta);
      if (next === base) return;

      pendingTargetRef.current = next;

      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
      wheelEndTimerRef.current = setTimeout(() => {
        wheelEndTimerRef.current = null;
        const target = pendingTargetRef.current;
        pendingTargetRef.current = null;
        if (target !== null) commitZoom(target);
      }, PTZ_ZOOM_WHEEL_END_MS);
    },
    [clamp, commitZoom, getZoom, operable],
  );

  const attachWheel = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return () => undefined;
      el.addEventListener("wheel", onWheelNative, { passive: false });
      return () => el.removeEventListener("wheel", onWheelNative);
    },
    [onWheelNative],
  );

  useEffect(
    () => () => {
      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
    },
    [],
  );

  return { attachWheel };
}
